import React, { useEffect, useState } from 'react';
import type { Theme } from './slideTypes';

// Self-healing fallback for an unrecognised slide template.
//
// An unknown template almost always means ONE thing: the content is newer than
// the client — a long-lived tab is running an app bundle cached by the service
// worker from before this slide type existed (e.g. a student window from before
// Attune shipped rendering "Unknown slide template" where the facilitator
// should be). The old dead-end message taught the learner nothing and healed
// nothing; worse, clients cached from before the update-toast shipped have NO
// prompt machinery at all, so they stay stale forever without help.
//
// So instead of shrugging, self-heal: check for a waiting service worker,
// message it to take over (vite-plugin-pwa's generated SW listens for
// SKIP_WAITING even in 'prompt' mode), and reload once. A sessionStorage guard
// makes this strictly one-shot per template per session — if the reload didn't
// fix it (template genuinely unknown, e.g. corrupt data), the learner gets a
// calm manual message rather than a reload loop.
const guardKey = (template: string) => `kl-stale-reload:${template}`;

async function updateAndReload(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) {
      await reg.update().catch(() => {});
      const waiting = reg.waiting;
      if (waiting) {
        // Ask the waiting SW to activate, reload when it takes control.
        const reloaded = new Promise<void>((resolve) => {
          navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
        });
        waiting.postMessage({ type: 'SKIP_WAITING' });
        // controllerchange usually lands well under a second; cap the wait.
        await Promise.race([reloaded, new Promise((r) => setTimeout(r, 2500))]);
      }
    }
  } catch { /* best-effort — the plain reload below still helps uncontrolled pages */ }
  window.location.reload();
}

export function StaleClientSlide({ template, theme, isEditing }: { template: string; theme: Theme; isEditing: boolean }) {
  // If we've already tried a reload for this template this session, don't loop —
  // fall through to the manual message.
  const [autoHealing, setAutoHealing] = useState(false);

  useEffect(() => {
    if (isEditing) return; // never auto-reload the builder under an instructor
    let alreadyTried = false;
    try {
      alreadyTried = sessionStorage.getItem(guardKey(template)) === '1';
      if (!alreadyTried) sessionStorage.setItem(guardKey(template), '1');
    } catch { /* storage unavailable → behave as already-tried */ alreadyTried = true; }
    if (alreadyTried) return;
    setAutoHealing(true);
    void updateAndReload();
  }, [template, isEditing]);

  if (autoHealing) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2" style={{ color: theme.colors.textMuted }}>
        <div>Updating to the latest version…</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col items-center justify-center gap-3 px-8 text-center" style={{ color: theme.colors.textMuted }}>
      <div>This part of the lesson needs the latest version of the app.</div>
      <button
        type="button"
        onClick={() => void updateAndReload()}
        className="rounded-full px-4 py-1.5 text-sm font-medium"
        style={{ background: theme.colors.text, color: theme.colors.background }}
      >
        Refresh
      </button>
    </div>
  );
}
